import api from './api';

export interface CompraPendienteMaster {
  nroFormularioMaster: number;
  nroItemMaster: number;
  nroProveedor?: number;
  nombreProveedor?: string;
  codigoArticulo?: string;
  descripcionArticulo?: string;
  /** Código de tipo del formulario master (15 OC, 16 REM, 1/2 FC). Puede venir como string o número en JSON. */
  nroTipoFormularioMaster?: string | number | null;
  /** Letra–suc–nro del comprobante del documento master (OC). */
  letraComprobanteMaster?: string;
  nroSucComprobanteMaster?: number;
  nroComprobanteMaster?: number | null;
  /** ISO 8601, fecha emisión del comprobante master. */
  fechaEmisionComprobanteMaster?: string | null;
  cantidadPedida: number;
  cantidadFacturada: number;
  cantidadRemitida: number;
  cantidadCancelada: number;
  cantidadDevuelta: number;
  pendienteFacturar: number;
  pendienteRecibir: number;
  /** Item.PrecioLista de la línea master (costo bruto / lista). */
  precioListaMaster?: number | null;
  /** Item.PrecioUnitario de la línea master (neto ítem). */
  precioUnitarioMaster?: number | null;
  /** C / G en la línea master (gastos). */
  tipoProveedorMaster?: string | null;
  porcDesc1Master?: number | null;
  porcDesc2Master?: number | null;
  porcDesc3Master?: number | null;
  porcDesc4Master?: number | null;
  porcDesc5Master?: number | null;
}

export interface CompraHiloComprobante {
  nroFormulario: number;
  nroItem: number;
  nroTipoFormulario?: string | number;
  fechaEmision?: string;
  letra?: string;
  nroSuc: number;
  nroComprobante?: number;
  codigoArticulo?: string;
  descripcionArticulo?: string;
  cantidad: number;
}

export interface CompraHiloResponse {
  nroFormularioMaster: number;
  nroItemMaster: number;
  comprobantes: CompraHiloComprobante[];
}

/** 15 = OC, 16 = REM, 1/2 = FC, 3 = NC (compras). Acepta string o número (p. ej. 1 → FC). */
export function abrevTipoComprobanteCompraMaster(tipo?: string | number | null): string {
  const key = tipo === null || tipo === undefined || tipo === '' ? '' : String(tipo).trim();
  switch (key) {
    case '15':
      return 'OC';
    case '16':
      return 'REM';
    case '1':
    case '2':
      return 'FC';
    case '3':
      return 'NC';
    default:
      return '';
  }
}

function padComprobanteNum(n: number | null | undefined, len: number): string {
  if (n == null || Number.isNaN(Number(n))) {
    return ''.padStart(len, '0');
  }
  const v = Math.trunc(Number(n));
  return String(Math.max(0, v)).padStart(len, '0');
}

/** Comprobante de una fila del hilo (mismo patrón letra-suc-nro que el master). */
export function formatComprobanteHiloLine(c: CompraHiloComprobante): string {
  const tipoAbrev = abrevTipoComprobanteCompraMaster(c.nroTipoFormulario);
  const letra = (c.letra ?? '').trim() || 'X';
  const cuerpo = `${letra}-${padComprobanteNum(c.nroSuc, 5)}-${padComprobanteNum(c.nroComprobante, 8)}`;
  if (tipoAbrev) return `${tipoAbrev} ${cuerpo}`;
  if (c.nroTipoFormulario !== undefined && c.nroTipoFormulario !== null && String(c.nroTipoFormulario).trim() !== '') {
    return `[${c.nroTipoFormulario}] ${cuerpo}`;
  }
  return cuerpo;
}

/** Costo bruto sugerido: lista del master si hay, si no precio unitario. */
export function costoBrutoDesdePendienteMaster(p: CompraPendienteMaster): number {
  const lista = Number(p.precioListaMaster);
  const unit = Number(p.precioUnitarioMaster);
  if (Number.isFinite(lista) && lista > 0) return lista;
  if (Number.isFinite(unit) && unit > 0) return unit;
  return 0;
}

export function porcDescDesdePendienteMaster(p: CompraPendienteMaster): {
  porcDesc1: number;
  porcDesc2: number;
  porcDesc3: number;
  porcDesc4: number;
  porcDesc5: number;
} {
  return {
    porcDesc1: Number(p.porcDesc1Master) || 0,
    porcDesc2: Number(p.porcDesc2Master) || 0,
    porcDesc3: Number(p.porcDesc3Master) || 0,
    porcDesc4: Number(p.porcDesc4Master) || 0,
    porcDesc5: Number(p.porcDesc5Master) || 0,
  };
}

/** Remito: precio lista + % línea; si solo hay unitario neto, precio sin descuento en tabla. */
export function precioRemitoDesdePendienteMaster(p: CompraPendienteMaster): { precioUnitario: number; porcDesc: number } {
  const lista = Number(p.precioListaMaster);
  const unit = Number(p.precioUnitarioMaster);
  const porc1 = Number(p.porcDesc1Master) || 0;
  if (Number.isFinite(lista) && lista > 0) return { precioUnitario: lista, porcDesc: porc1 };
  if (Number.isFinite(unit) && unit > 0) return { precioUnitario: unit, porcDesc: 0 };
  return { precioUnitario: 0, porcDesc: 0 };
}

/** Cantidad sugerida al agregar desde pendientes. */
export function cantidadSugeridaDesdePendiente(p: CompraPendienteMaster): number {
  const pf = Number(p.pendienteFacturar) || 0;
  const pr = Number(p.pendienteRecibir) || 0;
  const m = Math.max(pf, pr);
  return m > 0 ? m : 1;
}

/** Cantidad ya facturada en FC de compra, neto de NC (disponible para devolver en una nueva NC). */
export function cantidadFacturadaNetaDisponible(p: CompraPendienteMaster): number {
  const fact = Number(p.cantidadFacturada) || 0;
  const dev = Number(p.cantidadDevuelta) || 0;
  return Math.max(0, Math.round((fact - dev) * 10000) / 10000);
}

/** Cantidad sugerida al agregar línea ya facturada (hacia nota de crédito). */
export function cantidadSugeridaDesdeFacturadoCompra(p: CompraPendienteMaster): number {
  const n = cantidadFacturadaNetaDisponible(p);
  return n > 0 ? n : 1;
}

export function formatFechaEmisionComprobanteMaster(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('es-AR');
}

/** Ej.: "OC X-00099-00999999" (suc 5 dígitos, nro 8 dígitos). */
export function formatComprobanteMasterDisplay(p: CompraPendienteMaster): string {
  const tipoAbrev = abrevTipoComprobanteCompraMaster(p.nroTipoFormularioMaster);
  const letraRaw = (p.letraComprobanteMaster ?? '').trim();
  const letra = letraRaw || 'X';
  const suc = p.nroSucComprobanteMaster;
  const nro = p.nroComprobanteMaster;

  const sinDatos =
    !tipoAbrev &&
    !letraRaw &&
    (suc == null || suc === undefined) &&
    (nro == null || nro === undefined);
  if (sinDatos) return '—';

  const cuerpo = `${letra}-${padComprobanteNum(suc, 5)}-${padComprobanteNum(nro, 8)}`;
  return tipoAbrev ? `${tipoAbrev} ${cuerpo}` : cuerpo;
}

const comprasSeguimientoService = {
  async getPendientesMaster(
    nroProveedor?: number,
    soloPendientes = true,
    incluirGastos = false
  ): Promise<CompraPendienteMaster[]> {
    const response = await api.get<{ items: CompraPendienteMaster[] }>('/api/ComprasSeguimiento/pendientes-master', {
      params: {
        nroProveedor: nroProveedor || undefined,
        soloPendientes,
        incluirGastos,
      },
    });
    return response.data.items || [];
  },

  async getHilo(nroFormularioMaster: number, nroItemMaster: number): Promise<CompraHiloResponse> {
    const response = await api.get<CompraHiloResponse>(
      `/api/ComprasSeguimiento/hilo/${nroFormularioMaster}/${nroItemMaster}`
    );
    return response.data;
  },
};

export default comprasSeguimientoService;
